import { useState, useEffect } from 'react';
import { getAnalyticsEvents } from '../../services/analyticsService';
import { getCollection } from '../../services/firestoreService';
import BarChart from '../charts/BarChart';
import PieChart from '../charts/PieChart';

const AdoptionTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [adoptionData, setAdoptionData] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const events = await getAnalyticsEvents(30);
        
        // Get total users
        const users = await getCollection('users', undefined, undefined, undefined, 10000);
        const totalUsers = users.length;

        const ASSISTANT_EVENTS = ['assistant_topic_added', 'assistant_search_query', 'assistant_query', 'ai_chat'];
        const assistantUsed = events.filter((e) => 
          ASSISTANT_EVENTS.includes(e.eventName) || e.category === 'user_preferences'
        ).length;

        const topicsAdded = events.filter((e) => 
          e.eventName === 'assistant_topic_added' || e.eventName.includes('topic_added')
        ).length;

        const learningPathsStarted = events.filter((e) => 
          e.eventName === 'learning_path_started' || e.eventName.includes('learning_path')
        ).length;

        const progressUpdated = events.filter((e) => 
          e.eventName === 'learning_progress_updated' || e.eventName.includes('progress')
        ).length;

        const categoriesUsed = events.filter((e) => 
          e.eventName === 'personalized_category_used' || e.eventName.includes('category')
        ).length;

        const onboardingCompleted = events.filter((e) => 
          e.eventName === 'onboarding_completed' || e.eventName.includes('onboarding')
        ).length;

        // Calculate adoption rates
        const assistantAdoptionRate = totalUsers > 0 ? (assistantUsed / totalUsers) * 100 : 0;
        const learningAdoptionRate = totalUsers > 0 ? (learningPathsStarted / totalUsers) * 100 : 0;
        const categoryAdoptionRate = totalUsers > 0 ? (categoriesUsed / totalUsers) * 100 : 0;
        const onboardingRate = totalUsers > 0 ? (onboardingCompleted / totalUsers) * 100 : 0;

        // Unique users per feature
        const uniqueAssistantUsers = new Set(
          events.filter((e) => ASSISTANT_EVENTS.includes(e.eventName)).map((e) => e.userId)
        ).size;

        const uniqueLearningUsers = new Set(
          events.filter((e) => e.eventName.includes('learning')).map((e) => e.userId)
        ).size;

        setAdoptionData({
          totalUsers,
          assistantUsed,
          topicsAdded,
          learningPathsStarted,
          progressUpdated,
          categoriesUsed,
          onboardingCompleted,
          assistantAdoptionRate,
          learningAdoptionRate,
          categoryAdoptionRate,
          onboardingRate,
          uniqueAssistantUsers,
          uniqueLearningUsers,
        });
      } catch (error) {
        console.error('Error loading adoption data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading adoption metrics...</p>
      </div>
    );
  }

  if (!adoptionData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No adoption data available</p>
      </div>
    );
  }

  const featureUsageData = {
    labels: ['Assistant', 'Topics Added', 'Learning Paths', 'Progress Updates', 'Categories'],
    datasets: [
      {
        label: 'Usage Count',
        data: [
          adoptionData.assistantUsed,
          adoptionData.topicsAdded,
          adoptionData.learningPathsStarted,
          adoptionData.progressUpdated,
          adoptionData.categoriesUsed,
        ],
        backgroundColor: ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'],
      },
    ],
  };

  const adoptionRatesData = {
    labels: ['Assistant', 'Learning', 'Categories', 'Onboarding'],
    datasets: [
      {
        label: 'Adoption Rate (%)',
        data: [
          adoptionData.assistantAdoptionRate,
          adoptionData.learningAdoptionRate,
          adoptionData.categoryAdoptionRate,
          adoptionData.onboardingRate,
        ],
        backgroundColor: ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6'],
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Adoption Metrics
      </h1>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Total Users</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {adoptionData.totalUsers}
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Assistant Adoption</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {adoptionData.assistantAdoptionRate.toFixed(1)}%
          </h2>
          <p style={{ margin: 0, color: '#999', fontSize: '0.75rem' }}>
            {adoptionData.uniqueAssistantUsers} unique users
          </p>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Learning Adoption</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {adoptionData.learningAdoptionRate.toFixed(1)}%
          </h2>
          <p style={{ margin: 0, color: '#999', fontSize: '0.75rem' }}>
            {adoptionData.uniqueLearningUsers} unique users
          </p>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Onboarding Rate</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {adoptionData.onboardingRate.toFixed(1)}%
          </h2>
        </div>
      </div>

      {/* Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Feature Usage</h3>
          <BarChart data={featureUsageData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Adoption Rates</h3>
          <PieChart data={adoptionRatesData} height={300} />
        </div>
      </div>

      {/* Feature Details Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}>
        <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
          Feature Adoption Details
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Feature</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Usage Count</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Unique Users</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Adoption Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '1rem' }}>AI Assistant</td>
              <td style={{ padding: '1rem' }}>{adoptionData.assistantUsed}</td>
              <td style={{ padding: '1rem' }}>{adoptionData.uniqueAssistantUsers}</td>
              <td style={{ padding: '1rem', color: '#10b981' }}>
                {adoptionData.assistantAdoptionRate.toFixed(2)}%
              </td>
            </tr>
            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '1rem' }}>Topics Added</td>
              <td style={{ padding: '1rem' }}>{adoptionData.topicsAdded}</td>
              <td style={{ padding: '1rem' }}>-</td>
              <td style={{ padding: '1rem' }}>-</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '1rem' }}>Learning Paths</td>
              <td style={{ padding: '1rem' }}>{adoptionData.learningPathsStarted}</td>
              <td style={{ padding: '1rem' }}>{adoptionData.uniqueLearningUsers}</td>
              <td style={{ padding: '1rem', color: '#10b981' }}>
                {adoptionData.learningAdoptionRate.toFixed(2)}%
              </td>
            </tr>
            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '1rem' }}>Progress Updates</td>
              <td style={{ padding: '1rem' }}>{adoptionData.progressUpdated}</td>
              <td style={{ padding: '1rem' }}>-</td>
              <td style={{ padding: '1rem' }}>-</td>
            </tr>
            <tr>
              <td style={{ padding: '1rem' }}>Personalized Categories</td>
              <td style={{ padding: '1rem' }}>{adoptionData.categoriesUsed}</td>
              <td style={{ padding: '1rem' }}>-</td>
              <td style={{ padding: '1rem', color: '#10b981' }}>
                {adoptionData.categoryAdoptionRate.toFixed(2)}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdoptionTab;

